import {
  createBffClient,
  createBffClientV1,
  createBffClientV3,
} from 'bff-api/mitt-malarenergi';
import { addErrorStatus } from './bffRequestError';
import userManager from './userManager';

export { BffRequestError } from './bffRequestError';

function getBffBaseUrl(): string {
  const restApiUrl = import.meta.env.VITE_APP_REST_API_URL;
  try {
    const url = new URL(restApiUrl);
    return url.origin;
  } catch {
    return restApiUrl.replace(/\/api\/v\d+\/?$/, '');
  }
}

async function getAccessToken(): Promise<string> {
  const user = await userManager.getUser();
  if (user && !user.expired) {
    return user.access_token;
  }
  const refreshedUser = await userManager.signinSilent();
  return refreshedUser?.access_token ?? '';
}

export async function createMittBffClient() {
  return addErrorStatus(
    createBffClient({
      baseUrl: getBffBaseUrl(),
      auth: getAccessToken,
    }),
  );
}

export async function createMittBffClientV1() {
  return addErrorStatus(
    createBffClientV1({
      baseUrl: getBffBaseUrl(),
      auth: getAccessToken,
    }),
  );
}

export async function createMittBffClientV3() {
  return addErrorStatus(
    createBffClientV3({
      baseUrl: getBffBaseUrl(),
      auth: getAccessToken,
    }),
  );
}

export async function createAnonymousMittBffClient(customFetch?: typeof fetch) {
  return addErrorStatus(
    createBffClient({
      baseUrl: getBffBaseUrl(),
      fetch: customFetch,
    }),
  );
}

type MittBffClient = Awaited<ReturnType<typeof createMittBffClient>>;
type MittBffClientV1 = Awaited<ReturnType<typeof createMittBffClientV1>>;
type MittBffClientV3 = Awaited<ReturnType<typeof createMittBffClientV3>>;

/**
 * Creates a v2 BFF client, runs `request` against it and unwraps the
 * resulting `data`. Pass `throwOnError: true` in the generated SDK call so
 * `data` is guaranteed to be present and React Query surfaces failures via
 * its normal `isError`/`error` state.
 */
export async function bffRequest<TData>(
  request: (client: MittBffClient) => Promise<{ data: TData }>,
): Promise<TData> {
  const client = await createMittBffClient();
  const { data } = await request(client);
  return data;
}

export async function bffRequestV1<TData>(
  request: (client: MittBffClientV1) => Promise<{ data: TData }>,
): Promise<TData> {
  const client = await createMittBffClientV1();
  const { data } = await request(client);
  return data;
}

export async function bffRequestV3<TData>(
  request: (client: MittBffClientV3) => Promise<{ data: TData }>,
): Promise<TData> {
  const client = await createMittBffClientV3();
  const { data } = await request(client);
  return data;
}
